// Marketing video slots for the landing page.
//
// The videos are recorded by the founder and dropped in later. Until an
// asset exists, each slot renders a quiet "coming soon" placeholder (see
// components/VideoPlayer.tsx) instead of a broken player. Wiring a real
// asset is a one-line change here: set `src` (self-hosted MP4 in /public)
// or `youtubeId` (for the YouTube facade). No layout shift either way.
//
// Copy here is DRAFT and part of the pre-launch wording review
// (docs/landing-copy-review.md). Keep it within DESIGN-LANGUAGE voice:
// no exclamation points, no em dashes, no score promises.

export type MarketingVideoId = "founder_intro" | "product_demo" | "review_walkthrough";

export interface MarketingVideo {
  id: MarketingVideoId;
  // Short label used for analytics (video_play { which }) and aria text.
  title: string;
  // Self-hosted MP4 path under /public, or null until recorded.
  src: string | null;
  // Poster/thumbnail path under /public, or null. Shown before play and
  // as the still image on the placeholder card.
  poster: string | null;
  // YouTube video id for the lite facade (used for the walkthrough so it
  // doubles as channel growth), or null until published.
  youtubeId: string | null;
  // WebVTT captions file under /public for the self-hosted player, or
  // null. Native video controls expose a CC toggle when present.
  captions: string | null;
  // Text shown on the placeholder card while the asset is pending.
  placeholder: string;
}

export const MARKETING_VIDEOS: Record<MarketingVideoId, MarketingVideo> = {
  // NOT on the site yet (founder direction 2026-07-01: exactly one video
  // on the page, the product demo). Slot kept for when that changes.
  founder_intro: {
    id: "founder_intro",
    title: "Founder intro",
    src: null,
    poster: null,
    youtubeId: null,
    captions: null,
    placeholder: "A short intro from Isaiah is coming soon.",
  },
  // The one live slot: a quick demo of Isaiah logging a missed question.
  // 1:30, 1920x1080 H.264 (~7.6 MB); poster is a frame from the video.
  // Captions were auto-transcribed (Whisper) then brand-corrected; the
  // text is DRAFT copy like everything else on the landing surface.
  product_demo: {
    id: "product_demo",
    title: "Product demo",
    src: "/marketing/demo-log-question.mp4",
    poster: "/marketing/demo-log-question-poster.webp",
    youtubeId: null,
    captions: "/marketing/demo-log-question.en.vtt",
    placeholder: "A quick demo of logging a missed question is coming soon.",
  },
  // NOT on the site yet (same founder direction). Slot kept for later.
  review_walkthrough: {
    id: "review_walkthrough",
    title: "Review a missed question",
    src: null,
    poster: null,
    youtubeId: null,
    captions: null,
    placeholder: "Isaiah reviews a missed MCAT question. Coming soon.",
  },
};

// True once a real asset is attached (either a self-hosted file or a
// YouTube id). Drives whether VideoPlayer shows the player or the
// placeholder, and whether callers wire a video CTA.
export function isVideoReady(v: MarketingVideo): boolean {
  return Boolean(v.src || v.youtubeId);
}
